import React, { useEffect, useState, useCallback } from "react";
import { authFetch, C } from "./App";

export function IncidentTypesView({ token }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null); // type object or "new"
  const [actingId, setActingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    authFetch("/admin/incident-types", token)
      .then((d) => setTypes(d.types || []))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  function toggleActive(type) {
    setActingId(type.id);
    authFetch(`/admin/incident-types/${type.id}`, token, {
      method: "PATCH",
      body: JSON.stringify({ is_active: !type.is_active }),
    })
      .then(load)
      .catch((e) => setErr(e.message))
      .finally(() => setActingId(null));
  }

  function remove(id, name) {
    if (!window.confirm(`Delete "${name}"? Existing incidents already using this type keep it — this only removes it from the report form and filters. To retire a type still in use, use the toggle instead.`)) return;
    authFetch(`/admin/incident-types/${id}`, token, { method: "DELETE" })
      .then(load)
      .catch((e) => setErr(e.message));
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111", margin: 0 }}>Incident Types</h1>
        <button onClick={() => setEditing("new")} style={{ background: C.green, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>+ Add Type</button>
      </div>
      <p style={{ fontSize: 13, color: C.gray, margin: "0 0 20px" }}>
        Controls the options shown in the citizen app's Report form and filter dropdowns. Renaming an
        existing type doesn't touch incidents already reported with the old name — to rename in place
        for the future while leaving history intact, deactivate the old entry and add a new one.
      </p>

      {err && <div style={{ color: C.danger, fontSize: 13, marginBottom: 14 }}>{err}</div>}

      {loading ? (
        <div style={{ color: C.gray, fontSize: 13 }}>Loading…</div>
      ) : types.length === 0 ? (
        <div style={{ color: C.gray, fontSize: 13 }}>No incident types yet. Click "+ Add Type" to create one.</div>
      ) : (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: C.grayLight, textAlign: "left" }}>
                <Th>Name</Th><Th>Sort Order</Th><Th>Status</Th><Th>Added</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {types.map((tp) => (
                <tr key={tp.id} style={{ borderTop: `1px solid ${C.border}` }}>
                  <Td><b>{tp.name}</b></Td>
                  <Td style={{ color: C.gray }}>{tp.sort_order}</Td>
                  <Td>
                    <button
                      onClick={() => toggleActive(tp)}
                      disabled={actingId === tp.id}
                      style={{
                        fontSize: 10.5, fontWeight: 600, border: "none", borderRadius: 8, padding: "3px 9px", cursor: actingId === tp.id ? "default" : "pointer",
                        color: tp.is_active ? "#2E7D32" : "#777",
                        background: tp.is_active ? "#E8F5E9" : C.grayLight,
                        opacity: actingId === tp.id ? 0.5 : 1,
                      }}
                      title="Click to toggle whether this type appears in the citizen app"
                    >
                      {tp.is_active ? "ACTIVE" : "INACTIVE"}
                    </button>
                  </Td>
                  <Td style={{ color: C.gray }}>{new Date(tp.created_at).toLocaleDateString()}</Td>
                  <Td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => setEditing(tp)} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, padding: "5px 10px", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>Edit</button>
                      <button onClick={() => remove(tp.id, tp.name)} style={{ background: "transparent", border: "1px solid #C6282855", color: "#C62828", borderRadius: 7, padding: "5px 10px", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>Delete</button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <TypeEditModal
          token={token} type={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function Th({ children }) { return <th style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, color: C.gray, textTransform: "uppercase", letterSpacing: 0.5 }}>{children}</th>; }
function Td({ children, style }) { return <td style={{ padding: "10px 14px", ...style }}>{children}</td>; }

function TypeEditModal({ token, type, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: type?.name || "",
    sort_order: type?.sort_order ?? 0,
    is_active: type?.is_active ?? true,
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true); setErr("");
    const body = JSON.stringify({
      name: form.name.trim(),
      sort_order: form.sort_order === "" ? 0 : Number(form.sort_order),
      is_active: form.is_active,
    });
    const call = type
      ? authFetch(`/admin/incident-types/${type.id}`, token, { method: "PATCH", body })
      : authFetch("/admin/incident-types", token, { method: "POST", body });
    call.then(onSaved).catch((e) => setErr(e.message)).finally(() => setBusy(false));
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ background: "#fff", borderRadius: 14, padding: 24, width: 400, maxHeight: "88vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{type ? "Edit Incident Type" : "Add Incident Type"}</div>
        {err && <div style={{ color: C.danger, fontSize: 12.5, marginBottom: 12 }}>{err}</div>}

        <Field label="Name" required>
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required style={inputStyle} placeholder="e.g. Vehicle Theft"
          />
        </Field>

        <Field label="Sort Order">
          <input
            type="number" value={form.sort_order}
            onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
            style={inputStyle}
          />
          <div style={{ fontSize: 10.5, color: C.gray, marginTop: 4 }}>Lower numbers appear first in the dropdown.</div>
        </Field>

        <label style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, cursor: "pointer", marginTop: 6, marginBottom: 20, background: form.is_active ? "#F3F8F5" : "#fff" }}>
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
          <span>Active</span>
          <span style={{ color: C.gray, fontSize: 11.5 }}>(shown in the citizen app's report form and filters)</span>
        </label>

        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={onClose} style={{ flex: 1, background: C.grayLight, border: "none", borderRadius: 8, padding: "10px 0", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button type="submit" disabled={busy} style={{ flex: 1, background: C.green, color: "#fff", border: "none", borderRadius: 8, padding: "10px 0", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{busy ? "Saving…" : "Save Type"}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 11.5, fontWeight: 600, color: C.gray, marginBottom: 5, textTransform: "uppercase" }}>
        {label}{required && <span style={{ color: C.danger }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = { width: "100%", boxSizing: "border-box", padding: "9px 12px", border: "1px solid #e4e7ed", borderRadius: 8, fontSize: 13 };
